<template>
  <Loading v-if="!didLoadData"/>
  <div v-else>
    <!-- Dialogs -->
    <ConfirmDialog
      :model="showDeleteDialog" 
      :onConfirm="attemptDeleteAnnouncement"
      :onCancel="() => announcementToDelete = undefined">
      <div v-if="announcementToDelete">{{`Přejete si smazat oznámení s titulkem "${announcementToDelete.title}"?`}}</div>
      Tato operace je nevratná.
    </ConfirmDialog>
    <Alert 
      :model="errorOnDelete" 
      :onConfirm="() => errorOnDelete = false">
      Zvolené oznámení se nepodařilo smazat.
    </Alert>
    <BackButton :target="{name: 'unitDetail'}">Informační panely</BackButton>

      <!-- Module header -->
      <ModuleHeader v-if="getLocalizedAnnouncements?.length > 0">
        <RefreshButton @click="refreshAnnouncements" title="Aktualizovat data"><h2>Oznámení{{didLoadAdministrators ? ` - ${selectedUnit.localizedAttributes.cz.name}` : ''}}</h2></RefreshButton>
        <PrimaryButton id="add-announcement" :to="{name: 'announcementDetail', params: {announcementId: 'new'}}">+ Nové</PrimaryButton>                
      </ModuleHeader>
      <v-container v-if="getLocalizedAnnouncements?.length > 0">
        <!-- List of announcements -->
        <v-row>
          <v-col class=" d-sm-block pt-0" lg="4" md="6" sm="12">
            <AnnouncementTypeSelect 
              :announcementTypes="getAnnouncementTypes"
              :clearable="true"
              :value="search"
              v-on:change="convertFilter"
            />
          </v-col>
        </v-row>
        <v-data-table
          class="d-sm-block"
          :search="search"
          :custom-filter="announcementTypesFilter"
          :headers="headers"
          :items="getLocalizedAnnouncements"
          @update:sortBy="(value) => sortTitle(value)"
          :sort-by="sortBy"
          items-per-page="25"
          mobile-breakpoint="md"
        >
          <template v-slot:item.title="{ item }">
            <b id="announcement-title" v-bind:title="item.title" class="pointer light-green--text" @click="goToDetail(item.id)">
              {{ ellipsis(item.title, 40) }}
            </b>
          </template>
          <template v-slot:item.isPermanent="{ item }">
            <v-icon>{{item.isPermanent ? 'done' : 'block'}}</v-icon>
          </template>
          <template v-slot:item.blockParallel="{ item }">
            <v-icon>{{item.blockParallel ? 'block' : 'done'}}</v-icon>
          </template>
          <template v-slot:item.displayTo="{ item }">
            <span id="announcement-displayings" v-if="item.displayings?.length > 0">
              {{`od ${formatDate(item.displayFrom)} ${item.displayTo ? ` do ${formatDate(item.displayTo)}` : ''}`}}
            </span>
            <span v-else id="announcement-displayings" class="warning-text">nevybrán čas</span>
          </template>
          <template v-slot:item.announcementTypeId="{ item }">{{ getAnnouncementTypeNameFromId(item.announcementTypeId) }}</template>
          <template v-slot:item.action="{ item }">
            <v-icon id="edit-announcement" @click="goToDetail(item.id)" title="Upravit oznámení">tune</v-icon>
            <DeleteIcon id="remove-announcement" @click="() => announcementToDelete = item" title="Smazat oznámení"></DeleteIcon>
          </template>
        </v-data-table>
      </v-container>
      <v-container class="text-center mt-12" v-else>
        <img :src="Placeholder" id="no-announcements-placeholder" />
        <v-row>
            <v-col>
              <p class="mt-6 mb-10">Dosud nebyla vytvořena žádná oznámení...</p>
            </v-col>
        </v-row>
        <PrimaryButton id="add-announcement" :to="{name: 'announcementDetail', params: {announcementId: 'new'}}">+ Nové oznámení</PrimaryButton>
      </v-container>

  </div>
</template>


<script setup>
import {computed, onMounted, ref} from "vue";
import {announcementsHeaders} from "@/views/Displays/announcements/announcements-helpers";
import moment from "moment/moment";
import Loading from "@/components/Loading.vue";
import ConfirmDialog from "@/components/dialogs/ConfirmDialog.vue";
import Alert from "@/components/dialogs/Alert.vue";
import BackButton from "@/components/layout/BackButton.vue";
import ModuleHeader from "@/components/layout/ModuleHeader.vue";
import PrimaryButton from "@/components/buttons/PrimaryButton.vue";
import RefreshButton from "@/components/buttons/RefreshButton.vue";
import AnnouncementTypeSelect from "@/components/displays/AnnouncementTypeSelect.vue";
import {ellipsis} from "@/filters/ellipsis";
import DeleteIcon from "@/components/buttons/DeleteIcon.vue";
import {useRoute, useRouter} from "vue-router";
import {useStore} from "vuex";
import Placeholder from '../../../assets/displays/no_announcements.svg?url';


const router = useRouter()
const route = useRoute()
const store = useStore();

const headers = ref([...announcementsHeaders])
const announcementToDelete = ref(undefined)
const errorOnDelete = ref(false)
const sortBy = ref([])

onMounted(async ()=>{
  if (route.query.sortTitle) {
    sortTitle([{
      key: 'title',
      order: route.query.sortTitle
    }])
  }
  await store.dispatch('displaysAdmins/fetchAdministrators')
  await store.dispatch('announcements/fetchAnnouncements',route.params.unitId)
  await store.dispatch('announcementTypes/fetchAnnouncementTypes',route.params.unitId)
})


const didLoadAdministrators = computed(() => store.getters['displaysAdmins/didLoadAdministrators'])
const getAdministrators = computed(() => store.getters["displaysAdmins/getAdministrators"])
const getAnnouncementTypes = computed(() => store.getters["announcementTypes/getAnnouncementTypes"])
const didLoadAnnouncementTypes = computed(() => store.getters["announcementTypes/didLoadAnnouncementTypes"])
const getLocalizedAnnouncements = computed(() => store.getters["announcements/getLocalizedAnnouncements"])
const didLoadAnnouncements = computed(() => store.getters["announcements/didLoadAnnouncements"])
const username = computed(() => store.getters["user/username"])


const didLoadData = computed(()=>{
  return didLoadAnnouncements.value && didLoadAnnouncementTypes.value;
})

const showDeleteDialog = computed(()=>{
  return announcementToDelete.value !== undefined;
})

const search = computed(()=>{
  return route.query.search === undefined ? null : Number(route.query.search)
})

const selectedUnit = computed(()=>{
  return getAdministrators.value.find(user => user.xlogin === username.value)?.faculties?.find(faculty => faculty.id === Number(route.params.unitId));
})

function refreshAnnouncements() {
  store.dispatch('announcements/fetchAnnouncements',route.params.unitId)
  store.dispatch('announcementTypes/fetchAnnouncementTypes',route.params.unitId)
}

function sortTitle(value){
  sortBy.value = value
  router.replace({query: {
    ...route.query,
      sortTitle: value.length > 0 ? value[0].order : null
    }})
}

function announcementTypesFilter(value, search, item) {
  return search === null || item.columns.announcementTypeId === Number(search)
}

// select returns number but filter requires string
function convertFilter(value) {
  router.replace({query: {
      ...route.query,
      search: value === null ? undefined : value.toString(),
    }})
}

function attemptDeleteAnnouncement() {
  store.dispatch('announcements/deleteAnnouncement',announcementToDelete.value?.id).catch(() => {
    errorOnDelete.value = true;
  })
  announcementToDelete.value = undefined;
}

function goToDetail(announcementId) {
  router.push({
    name: "announcementDetail",
    params: {announcementId}
  })
}

function getAnnouncementTypeNameFromId(id) {
  return getAnnouncementTypes.value.find(type => type.id == id)?.name || "-";
}

function formatDate(timestamp) {
  return moment.unix(timestamp).format('ll');
}

</script>

<style scoped>
  .justify-center {
    display: -webkit-box;
  }

  .warning-text {
    color: rgb(255, 82, 82);
  }

  .container {
    margin-left: 0;
    margin-right: 0;
    padding-left: 0;
    padding-right: 0;
    min-width: 100%;
  }
</style>
